import axios from 'axios';
import { Coupon, DashboardStats, Plan, Subscription, Charge } from './_models';

const API_URL = process.env.REACT_APP_API_URL_BACKEND || 'http://localhost:3333/api/v1';

const PLAN_URL = `${API_URL}/plans`;
const SUBSCRIPTION_URL = `${API_URL}/subscriptions`;
const CHARGE_URL = `${API_URL}/charges`;
const RULER_URL = `${API_URL}/ruler-config`;
const GATEWAY_URL = `${API_URL}/gateway-config`;
const TRIGGER_URL = `${API_URL}/notification-triggers`;

export const getDashboardStats = (): Promise<DashboardStats> => {
  return axios.get(`${SUBSCRIPTION_URL}/statistics`).then((res) => {
    return res.data || {
      total_subscriptions: 0,
      monthly_revenue: 0,
      revenue_growth_percentage: 0,
      default_rate_percentage: 0,
      default_clients_count: 0,
      churn_rate_percentage: 0,
      churn_message: 'Dados reais em breve'
    };
  });
};

export const getSubscriptions = (): Promise<Subscription[]> => {
  return axios.get(`${SUBSCRIPTION_URL}`).then((res) => {
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  });
};

export const getPlans = (): Promise<Plan[]> => {
  return axios.get(`${PLAN_URL}?status=ATIVO`).then((res) => {
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  });
};

export const createPlan = (data: any): Promise<Plan> => {
  return axios.post(`${PLAN_URL}`, data).then((res) => res.data);
};

export const updatePlan = (id: string, data: any): Promise<Plan> => {
  return axios.put(`${PLAN_URL}/${id}`, data).then((res) => res.data);
};

export const deletePlan = (id: string): Promise<any> => {
  return axios.delete(`${PLAN_URL}/${id}`).then((res) => res.data);
};

export const getCoupons = (): Promise<Coupon[]> => {
  return axios.get(`${API_URL}/coupons`).then((res) => {
    const data = res.data;
    if (data && Array.isArray(data.coupons)) return data.coupons;
    if (Array.isArray(data)) return data;
    return [];
  });
};

export const createCoupon = (data: any): Promise<Coupon> => {
  return axios.post(`${API_URL}/coupons`, data).then((res) => res.data);
};

export const updateCoupon = (id: string, data: any): Promise<Coupon> => {
  return axios.put(`${API_URL}/coupons/${id}`, data).then((res) => res.data);
};

export const getSubscriptionByToken = (token: string): Promise<Subscription> => {
  return axios.get(`${SUBSCRIPTION_URL}/access/${token}`).then((res) => res.data);
};

export const checkoutSubscription = (token: string, data: any): Promise<any> => {
  return axios.post(`${SUBSCRIPTION_URL}/access/${token}/checkout`, data).then((res) => res.data);
};

export const validateCouponPublic = (token: string, coupon_code: string): Promise<any> => {
  return axios.post(`${SUBSCRIPTION_URL}/access/${token}/validate-coupon`, { coupon_code }).then((res) => res.data);
};

export const tokenizeCardByToken = (accessToken: string, payload: any): Promise<any> => {
  return axios.post(`${SUBSCRIPTION_URL}/access/${accessToken}/tokenize-card`, payload).then((res) => res.data);
};

export const createSubscription = (data: any): Promise<any> => {
  return axios.post(`${SUBSCRIPTION_URL}`, data).then((res) => res.data);
};

export const getCharges = (): Promise<Charge[]> => {
  return axios.get(`${CHARGE_URL}`).then((res) => {
    const data = res.data;
    if (data && Array.isArray(data.charges)) return data.charges;
    if (Array.isArray(data)) return data;
    return [];
  });
};

export const getChargesPagination = (limit = 50, page = 1, filters?: any): Promise<any> => {
  let query = `?limit=${limit}&page=${page}`;
  if (filters) {
    if (filters.status) query += `&status=${filters.status}`;
    if (filters.payment_method) query += `&payment_method=${filters.payment_method}`;
    if (filters.client_id) query += `&client_id=${filters.client_id}`;
    if (filters.reference_type) query += `&reference_type=${filters.reference_type}`;
    if (filters.due_date_start) query += `&due_date_start=${filters.due_date_start}`;
    if (filters.due_date_end) query += `&due_date_end=${filters.due_date_end}`;
  }
  return axios.get(`${CHARGE_URL}${query}`).then((res) => res.data);
};

export const createStandaloneCharge = (data: any): Promise<any> =>
  axios.post(`${CHARGE_URL}/standalone`, data).then((res) => res.data);

export const getChargeStatistics = (): Promise<any> => {
  return axios.get(`${CHARGE_URL}/statistics`).then((res) => res.data);
};

export const getRulerConfig = (): Promise<any> => {
  return axios.get(`${RULER_URL}`).then((res) => res.data);
};

export const updateRulerConfig = (data: any): Promise<any> => {
  return axios.put(`${RULER_URL}`, data).then((res) => res.data);
};

export const getGatewayConfig = (): Promise<any> => {
  return axios.get(`${GATEWAY_URL}`).then((res) => res.data);
};

export const updateGatewayConfig = (data: any): Promise<any> => {
  return axios.put(`${GATEWAY_URL}`, data).then((res) => res.data);
};

export const getNotificationTriggers = (): Promise<any[]> => {
  return axios.get(`${TRIGGER_URL}`).then((res) => {
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  });
};

export const createNotificationTrigger = (data: any): Promise<any> => {
  return axios.post(`${TRIGGER_URL}`, data).then((res) => res.data);
};

export const updateNotificationTrigger = (id: string, data: any): Promise<any> => {
  return axios.put(`${TRIGGER_URL}/${id}`, data).then((res) => res.data);
};

export const deleteNotificationTrigger = (id: string): Promise<any> => {
  return axios.delete(`${TRIGGER_URL}/${id}`).then((res) => res.data);
};
export const cancelCharge = (id: string): Promise<any> => {
  return axios.patch(`${CHARGE_URL}/${id}/cancel`).then((res) => res.data);
};

export const refundCharge = (id: string, reason: string): Promise<any> => {
  return axios.patch(`${CHARGE_URL}/${id}/refund`, { reason }).then((res) => res.data);
};

export const cancelSubscription = (id: string, reason: string): Promise<any> => {
  return axios.patch(`${SUBSCRIPTION_URL}/${id}/cancel`, { reason }).then((res) => res.data);
};

export const suspendSubscription = (id: string): Promise<any> => {
  return axios.patch(`${SUBSCRIPTION_URL}/${id}/suspend`).then((res) => res.data);
};

export const reactivateSubscription = (id: string): Promise<any> => {
  return axios.patch(`${SUBSCRIPTION_URL}/${id}/reactivate`).then((res) => res.data);
};

export const updateSubscription = (id: string, payload: any): Promise<any> => {
  return axios.put(`${SUBSCRIPTION_URL}/${id}`, payload).then((res) => res.data);
};
